import React, {useEffect, useMemo} from "react";
import {inject, observer} from "mobx-react";
import {Routes, Route, Navigate, useLocation} from "react-router-dom";
//local
import "./App.css";
import {
  MAIN_PAGE,
  ORGANIZATIONS,
  MAIN_SETTINGS,
  INVITE_COMPANIES,
  ONBOARDING,
  ID_IDENTIFY,
  OUR_FAVOURITES,
  FRAMEWORK_AGREEMENTS,
  JOINING_ORG,
  OUR_BLACKLIST,
  ORGANIZATION_ID,
  REQUEST,
  REQUEST_OFFER,
  CONTRACTS,
  CONTRACT,
  UPLOADED_CONTRACT,
  OUR_QUALIFICATIONS,
  REQUESTS_TO_US,
  REQUEST_TO_US,
  OUR_OFFERS,
  CONTRACTS_TO_US,
  CONTRACT_TO_US,
  PUBLIC_RFTS,
  PUBLIC_RFT,
  PROJECTS,
  PROJECT,
  INVITE_ONBOARDING,
  ORGANIZATION_USER_INVITE,
  ORGANIZATION_USER_INVITE_CHOICE,
  // PUBLIC SIDE
  RFP_OFFERS,
  SIGN_CONTRACTS,
  SIGN_CONTRACT,
  PUBLIC_SIGN_CONTRACT,
  SAVE_SEARCH_REGIONS,
  SAVE_SEARCH_SERVICE_CATEGORIES,
  EXTERNAL_RFT,
  LOGIN_LINK,
} from "./constants/routes.const";

import OnboardingPage from "./pages/OnboardingPage/OnboardingPage";
import IdentifyOwner from "./pages/IdentifyOwnerPage/IdentifyOwner";
import {PrivateRoute} from "./router/PrivateRoute";
import {InviteRoute} from "./router/InviteRoute";
import FindSuppliers from "./pages/FindSuppliersPage/FindSuppliers";
import InviteCompanies from "./pages/InviteCompaniesPage/InviteCompanies";
import OurSuppliersPage from "./pages/OurSuppliersPage/OurSuppliersPage";
import FrameworkAgreementsPage from "./pages/FrameworkAgreementsPage/FrameworkAgreementsPage";
import OurBlacklistPage from "./pages/OurBlacklist/OurBlacklistPage";
import {JoinOrganization} from "./pages/JoinOrganizationPage/JoinOrganization";
import RequestPage from "./pages/OurRequests/components/RequestPage";
import RequestsToUsPage from "./pages/RequestsToUs/RequestsToUsPage";
import RequestToUsPage from "./pages/RequestsToUs/RequestToUsPage";
import OurOffersPage from "./pages/OurOffers/OurOffersPage";
import ContractsPage from "./pages/Contracts/ContractsPage";
import ContractsToUsPage from "./pages/ContractsToUs/ContractsToUsPage";
import UploadedContractPage from "./pages/UploadedContracts/UploadedContractPage";
import PublicRfpsPage from "./pages/PublicRfps/PublicRfpsPage";
import OurQualificationsPage from "./pages/OurQualifications/OurQualificationsPage";
import OurProjectsPage from "./pages/OurProjects/OurProjectsPage";
import ProjectPage from "./pages/OurProjects/ProjectPage";
import PresentationPage from "./pages/PresentationPage/PresentationPage";
import InviteOnboarding from "./pages/InviteOnboardingPage/InviteOnboarding";
import OrganizationUserInvite from "./pages/OrganizationUserInvite/OrganizationUserInvite";
import InviteChoicePage from "./pages/OrganizationUserInvite/components/InviteChoicePage/InviteChoicePage";
import SignContractsPage from "./pages/SignContracts/SignContractsPage";
import SignContractPage from "./pages/SignContracts/SignContractPage";
// PUBLIC SIDE
import RFPOffersPage from "./pages/Public/RFPOffers/RFPOffersPage";
import MainPage from "pages/MainPage";
import {Preloader} from "components/Preloader";
import SearchPreferencesRegions from "pages/SaveSearchPreferences/SearchPreferencesRegions";
import SearchServiceCategories from "pages/SaveSearchPreferences/SearchServiceCategories";
import ExternalRftPage from "pages/PublicRfps/ExternalRftPage";
import LoginLinkPage from "pages/LoginLinkPage/LoginLinkPage";

// eslint-disable-next-line import/no-anonymous-default-export
export const App = inject("store")(
  observer(({store: {auth}}) => {
    const location = useLocation();
    const {organizationSubscription, token, user, loading} = auth;

    useEffect(() => {
      const searchParams = new URLSearchParams(location.search);
      const urlToken = searchParams.get("entraToken");

      if (urlToken && urlToken !== token) {
        auth.setToken(urlToken);

        searchParams.delete("entraToken");
        const newSearch = searchParams.toString();
        const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ""}${
          location.hash || ""
        }`;
        window.history.replaceState({}, "", newUrl);
      }
    }, [location.pathname, location.search, location.hash, token, auth]);

    // determining the user's preferred business role when logging into the application
    const userSubscriptions = organizationSubscription?.SubscribeProducts?.map(
      (item) => item.name
    );
    const isBuyer = useMemo(
      () =>
        userSubscriptions?.includes("SUBSCRIPTION_NAME_04") ||
        userSubscriptions?.includes("SUBSCRIPTION_NAME_05"),
      [userSubscriptions]
    );

    const isNotLoading = useMemo(
      () =>
        !!token
          ? !!user.id
            ? user.ActiveOrganization
              ? !!organizationSubscription
              : true
            : false
          : true,
      [token, organizationSubscription, user]
    );

    useEffect(() => {
      // the login link page logs in on its own; checking an old token first could end
      // its session and redirect away before the link is used
      if (window.location.pathname === LOGIN_LINK) return;
      auth.checkToken().catch(() => console.error());
    }, [auth, token]);

    if (location.pathname === LOGIN_LINK) return <LoginLinkPage />;

    return (
      <div className="app">
        {isNotLoading ? (
          <Routes>
            <Route
              path={MAIN_PAGE}
              element={
                !!token ? (
                  <Navigate to={isBuyer ? PROJECTS : PUBLIC_RFTS} replace />
                ) : (
                  <MainPage />
                )
              }
            />

            <Route path={MAIN_SETTINGS} element={<PrivateRoute path={MAIN_SETTINGS} />} />
            <Route
              path={ONBOARDING}
              element={<PrivateRoute path={ONBOARDING} component={OnboardingPage} />}
            />
            <Route
              path={ID_IDENTIFY}
              element={<PrivateRoute path={ID_IDENTIFY} component={IdentifyOwner} />}
            />
            <Route
              path={SAVE_SEARCH_REGIONS}
              element={
                <PrivateRoute
                  path={SAVE_SEARCH_REGIONS}
                  component={SearchPreferencesRegions}
                />
              }
            />
            <Route
              path={SAVE_SEARCH_SERVICE_CATEGORIES}
              element={
                <PrivateRoute
                  path={SAVE_SEARCH_SERVICE_CATEGORIES}
                  component={SearchServiceCategories}
                />
              }
            />
            <Route
              path={OUR_FAVOURITES}
              element={
                <PrivateRoute path={OUR_FAVOURITES} component={OurSuppliersPage} />
              }
            />
            <Route
              path={FRAMEWORK_AGREEMENTS}
              element={
                <PrivateRoute
                  path={FRAMEWORK_AGREEMENTS}
                  component={FrameworkAgreementsPage}
                />
              }
            />
            <Route
              path={OUR_BLACKLIST}
              element={<PrivateRoute path={OUR_BLACKLIST} component={OurBlacklistPage} />}
            />
            <Route
              path={JOINING_ORG}
              element={<PrivateRoute path={JOINING_ORG} component={JoinOrganization} />}
            />
            <Route
              path={ORGANIZATION_ID}
              element={
                <PrivateRoute exact path={ORGANIZATION_ID} component={PresentationPage} />
              }
            />
            <Route
              path={REQUEST}
              element={<PrivateRoute exact path={REQUEST} component={RequestPage} />}
            />
            <Route
              path={REQUEST_OFFER}
              element={<PrivateRoute exact path={REQUEST} component={RequestPage} />}
            />
            <Route
              path={PUBLIC_RFTS}
              element={
                <PrivateRoute exact path={PUBLIC_RFTS} component={PublicRfpsPage} />
              }
            />
            <Route
              path={PUBLIC_RFT}
              element={
                <PrivateRoute exact path={PUBLIC_RFT} component={RequestToUsPage} />
              }
            />
            <Route
              path={OUR_QUALIFICATIONS}
              element={
                <PrivateRoute
                  exact
                  path={OUR_QUALIFICATIONS}
                  component={OurQualificationsPage}
                />
              }
            />
            <Route
              path={REQUESTS_TO_US}
              element={
                <PrivateRoute exact path={REQUESTS_TO_US} component={RequestsToUsPage} />
              }
            />
            <Route
              path={REQUEST_TO_US}
              element={
                <PrivateRoute exact path={REQUEST_TO_US} component={RequestToUsPage} />
              }
            />
            <Route
              path={OUR_OFFERS}
              element={<PrivateRoute exact path={OUR_OFFERS} component={OurOffersPage} />}
            />
            <Route
              path={CONTRACTS}
              element={<PrivateRoute exact path={CONTRACTS} component={ContractsPage} />}
            />
            <Route
              path={CONTRACT}
              element={<PrivateRoute exact path={CONTRACT} component={RequestPage} />}
            />
            <Route
              path={UPLOADED_CONTRACT}
              element={
                <PrivateRoute
                  exact
                  path={UPLOADED_CONTRACT}
                  component={UploadedContractPage}
                />
              }
            />
            <Route
              path={EXTERNAL_RFT}
              element={
                <PrivateRoute exact path={EXTERNAL_RFT} component={ExternalRftPage} />
              }
            />
            <Route
              path={CONTRACTS_TO_US}
              element={
                <PrivateRoute
                  exact
                  path={CONTRACTS_TO_US}
                  component={ContractsToUsPage}
                />
              }
            />
            <Route
              path={CONTRACT_TO_US}
              element={
                <PrivateRoute exact path={CONTRACT_TO_US} component={RequestToUsPage} />
              }
            />
            <Route
              path={PROJECTS}
              element={<PrivateRoute exact path={PROJECTS} component={OurProjectsPage} />}
            />
            <Route
              path={PROJECT}
              element={<PrivateRoute exact path={PROJECT} component={ProjectPage} />}
            />
            <Route
              path={ORGANIZATIONS}
              element={
                <PrivateRoute exact path={ORGANIZATIONS} component={FindSuppliers} />
              }
            />
            <Route
              path={INVITE_COMPANIES}
              element={
                <PrivateRoute exact path={INVITE_COMPANIES} component={InviteCompanies} />
              }
            />

            {/* SIGN CONTRACTS */}
            <Route
              path={SIGN_CONTRACTS}
              element={
                <PrivateRoute exact path={SIGN_CONTRACTS} component={SignContractsPage} />
              }
            />
            <Route
              path={SIGN_CONTRACT}
              element={
                <PrivateRoute exact path={SIGN_CONTRACT} component={SignContractPage} />
              }
            />

            {/* PUBLIC SIDE */}
            <Route
              path={RFP_OFFERS}
              element={<PrivateRoute exact path={RFP_OFFERS} component={RFPOffersPage} />}
            />
            <Route
              path={PUBLIC_SIGN_CONTRACT}
              element={
                <PrivateRoute
                  exact
                  path={PUBLIC_SIGN_CONTRACT}
                  component={SignContractPage}
                />
              }
            />
            <Route path={INVITE_ONBOARDING} element={<InviteOnboarding />} />
            <Route
              path={ORGANIZATION_USER_INVITE}
              element={
                <InviteRoute
                  exact
                  path={ORGANIZATION_USER_INVITE}
                  component={OrganizationUserInvite}
                />
              }
            />
            <Route
              path={ORGANIZATION_USER_INVITE_CHOICE}
              element={
                <InviteRoute
                  exact
                  path={ORGANIZATION_USER_INVITE_CHOICE}
                  component={InviteChoicePage}
                />
              }
            />

            <Route path="*" element={<h1 className="error-page_title">404</h1>} />
          </Routes>
        ) : loading ? (
          <Preloader />
        ) : null}
      </div>
    );
  })
);
